/**
 * UI Event Tracking
 *
 * Type-safe event tracking helpers for general UI interactions.
 * Tracks navigation, button clicks, and user engagement.
 */

import { trackEvent } from './eventTracker';

/**
 * Type-safe UI event tracking helpers
 */
export const trackUIEvent = {
    /**
     * Track button click events
     */
    buttonClicked: (
        buttonId: string,
        buttonText: string,
        section?: string
    ) => trackEvent('button_clicked', {
        buttonId,
        buttonText,
        section,
    }),

    /**
     * Track navigation link clicks
     */
    linkClicked: (
        linkText: string,
        destination: string,
        section?: string
    ) => trackEvent('link_clicked', {
        linkText,
        destination,
        section,
    }),

    /**
     * Track when user opens a modal/dialog
     */
    modalOpened: (modalName: string) =>
        trackEvent('modal_opened', { modalName }),

    /**
     * Track when user closes a modal/dialog
     */
    modalClosed: (modalName: string, action?: string) =>
        trackEvent('modal_closed', { modalName, action }),

    /**
     * Track tab/section changes
     */
    tabChanged: (tabName: string, previousTab?: string) =>
        trackEvent('tab_changed', { tabName, previousTab }),

    /**
     * Track accordion/collapsible expand
     */
    sectionExpanded: (sectionName: string) =>
        trackEvent('section_expanded', { sectionName }),

    /**
     * Track accordion/collapsible collapse
     */
    sectionCollapsed: (sectionName: string) =>
        trackEvent('section_collapsed', { sectionName }),

    /**
     * Track search queries
     */
    searchPerformed: (query: string, resultCount: number) =>
        trackEvent('search_performed', { query, resultCount }),

    /**
     * Track filter applied
     */
    filterApplied: (filterType: string, filterValue: string) =>
        trackEvent('filter_applied', { filterType, filterValue }),

    /**
     * Track filter cleared
     */
    filterCleared: (filterType: string) =>
        trackEvent('filter_cleared', { filterType }),

    /**
     * Track sort option changed
     */
    sortChanged: (sortBy: string, sortOrder?: 'asc' | 'desc') =>
        trackEvent('sort_changed', { sortBy, sortOrder }),

    /**
     * Track form field focus (useful for understanding where users abandon)
     */
    fieldFocused: (formName: string, fieldName: string) =>
        trackEvent('field_focused', { formName, fieldName }),

    /**
     * Track form field blur without value (potential abandonment point)
     */
    fieldAbandoned: (formName: string, fieldName: string) =>
        trackEvent('field_abandoned', { formName, fieldName }),

    /**
     * Track form validation error
     */
    validationError: (formName: string, fieldName: string, errorType: string) =>
        trackEvent('validation_error', { formName, fieldName, errorType }),

    /**
     * Track external link clicks (leaving the site)
     */
    externalLinkClicked: (url: string, linkText: string) =>
        trackEvent('external_link_clicked', { url, linkText }),

    /**
     * Track social share button clicks
     */
    socialShareClicked: (platform: string, contentType: string) =>
        trackEvent('social_share_clicked', { platform, contentType }),

    /**
     * Track help/support actions
     */
    helpClicked: (helpType: string, context?: string) =>
        trackEvent('help_clicked', { helpType, context }),

    /**
     * Track scroll depth milestones
     */
    scrollDepthReached: (depth: number, pageSection?: string) =>
        trackEvent('scroll_depth_reached', { depth, pageSection }),

    /**
     * Track image gallery interactions
     */
    galleryImageViewed: (imageIndex: number, productId?: string) =>
        trackEvent('gallery_image_viewed', { imageIndex, productId }),

    /**
     * Track video interactions
     */
    videoInteraction: (
        action: 'play' | 'pause' | 'complete' | 'seek',
        videoId: string,
        timestamp?: number
    ) => trackEvent('video_interaction', { action, videoId, timestamp }),
};
